import React from 'react';

import {
  EvidenceNeededGuidanceCard,
  buildTargetedValidationEvidenceGuidance,
} from '../evidenceNeededGuidance';

const DEFAULT_COVERAGE_NOTE = 'Validation status is based on attached result evidence.';

function emptyModeCounts() {
  return {
    auto_checkable: 0,
    human_review_required: 0,
    external_runner_required: 0,
  };
}

function fallbackFindingTargetedValidation(targetedValidation) {
  const eligibilityStatus = targetedValidation?.eligibility_status || 'not_eligible';
  return {
    eligibility_status: eligibilityStatus,
    eligibility_reason:
      targetedValidation?.eligibility_reason || 'Targeted validation data is not available for this finding yet.',
    follow_up_route:
      targetedValidation?.follow_up_route
      || (targetedValidation?.active_pack_id || eligibilityStatus === 'eligible'
        ? 'in_app_pack'
        : eligibilityStatus === 'insufficient_provenance'
          ? 'unavailable'
          : 'external_validation'),
    preparedness_status: targetedValidation?.preparedness_status || 'none',
    active_pack_id: targetedValidation?.active_pack_id || null,
    validation_mode_counts: targetedValidation?.validation_mode_counts || emptyModeCounts(),
    coverage_note: targetedValidation?.coverage_note || DEFAULT_COVERAGE_NOTE,
    result_run_count: targetedValidation?.result_run_count || 0,
    latest_result_run_id: targetedValidation?.latest_result_run_id || null,
    latest_result_summary: targetedValidation?.latest_result_summary || null,
  };
}

function fallbackWorkflowTargetedValidationSummary(summary) {
  return {
    eligible_finding_count: summary?.eligible_finding_count || 0,
    active_pack_count: summary?.active_pack_count || 0,
    packs_needing_labels_count: summary?.packs_needing_labels_count || 0,
    ready_pack_count: summary?.ready_pack_count || 0,
    validated_pack_count: summary?.validated_pack_count || 0,
  };
}

function validationModeLabel(mode) {
  switch (mode) {
    case 'auto_checkable':
      return 'Auto-checkable';
    case 'human_review_required':
      return 'Needs human review';
    case 'external_runner_required':
      return 'Needs external evaluation';
    default:
      return mode || 'Unknown';
  }
}

function validationPostureLabel(posture) {
  switch (posture) {
    case 'in_app_auto_check':
      return 'Auto-checkable';
    case 'human_review_required':
      return 'Human review';
    case 'external_evaluation_required':
      return 'External evaluation';
    case 'diagnostic_only':
      return 'Diagnostic only';
    default:
      return posture ? posture.replaceAll('_', ' ') : 'Reference guidance';
  }
}

function expectedMovementLabel(direction) {
  switch (direction) {
    case 'decrease':
      return 'Should decrease';
    case 'increase':
      return 'Should increase';
    case 'stabilize':
      return 'Should stabilize';
    default:
      return direction ? direction.replaceAll('_', ' ') : 'Expected movement pending';
  }
}

function ReferenceValidationGuidance({ reference, compact = false }) {
  if (!reference) {
    return null;
  }
  const nextEvidence = reference.next_evidence_needed || [];
  return (
    <div className="targeted-validation-reference eval-pack-guidance-block">
      <div className="workflow-mode-header">
        <strong>{compact ? 'Validation posture' : 'Reference validation posture'}</strong>
        <span className="chip targeted-validation-meta-chip">{validationPostureLabel(reference.validation_posture)}</span>
      </div>
      <p>
        {reference.compare_metric_label || reference.compare_metric} · {expectedMovementLabel(reference.expected_movement_direction)}
      </p>
      {nextEvidence.length ? (
        <div className="eval-pack-guidance-block">
          <strong>Evidence needed</strong>
          <ul className="eval-pack-guidance-list">
            {nextEvidence.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

function resultStatusLabel(status) {
  switch (status) {
    case 'completed':
      return 'Completed';
    case 'partial':
      return 'Partial';
    case 'failed':
      return 'Failed';
    default:
      return status || 'Unknown';
  }
}

function formatPercent(value) {
  if (typeof value !== 'number') {
    return 'n/a';
  }
  return `${Math.round(value * 100)}%`;
}

function preparednessLabel(status) {
  switch (status) {
    case 'needs_labels':
      return 'Needs labels';
    case 'ready':
      return 'Ready';
    case 'validated':
      return 'Validated';
    case 'none':
    default:
      return 'No pack yet';
  }
}

function eligibilityLabel(status) {
  switch (status) {
    case 'eligible':
      return 'Eligible';
    case 'insufficient_provenance':
      return 'Insufficient provenance';
    case 'not_eligible':
    default:
      return 'Not eligible';
  }
}

function ModeChips({ counts }) {
  const entries = [
    ['auto_checkable', counts?.auto_checkable || 0],
    ['human_review_required', counts?.human_review_required || 0],
    ['external_runner_required', counts?.external_runner_required || 0],
  ].filter(([, value]) => value > 0);

  if (!entries.length) {
    return null;
  }

  return (
    <div className="chip-row">
      {entries.map(([mode, value]) => (
        <span className="chip targeted-validation-meta-chip" key={mode}>
          {validationModeLabel(mode)} {value}
        </span>
      ))}
    </div>
  );
}

function ValidationRunStatusCard({ targetedValidation }) {
  const latestResult = targetedValidation.latest_result_summary;
  const latestStatus = latestResult ? resultStatusLabel(latestResult.status) : 'No run yet';
  const passRate =
    latestResult && typeof latestResult.pass_rate === 'number' ? formatPercent(latestResult.pass_rate) : 'Not available';

  return (
    <div className="detail-card targeted-validation-evidence-card">
      <div className="workflow-mode-header">
        <strong>Validation run status</strong>
        <span className="chip targeted-validation-meta-chip">
          {latestResult ? latestStatus : targetedValidation.result_run_count > 0 ? 'Evidence attached' : 'Awaiting results'}
        </span>
      </div>
      <div className="detail-metrics-grid targeted-validation-status-grid">
        <div className="targeted-validation-status-metric">
          <strong>{targetedValidation.result_run_count}</strong>
          <p>Attached runs</p>
        </div>
        <div className="targeted-validation-status-metric">
          <strong>{latestStatus}</strong>
          <p>Latest run</p>
        </div>
        <div className="targeted-validation-status-metric">
          <strong>{passRate}</strong>
          <p>Pass rate</p>
        </div>
      </div>
      {targetedValidation.latest_result_run_id ? (
        <p className="overview-note">Latest result run {targetedValidation.latest_result_run_id}</p>
      ) : (
        <p className="overview-note">Run the validation pack to attach result evidence.</p>
      )}
    </div>
  );
}

function externalValidationStatusLabel(targetedValidation) {
  if (targetedValidation?.latest_result_summary || targetedValidation?.preparedness_status === 'validated') {
    return 'Validated';
  }
  if ((targetedValidation?.result_run_count || 0) > 0) {
    return 'Evidence attached';
  }
  return 'Recommended';
}

function primaryRecommendation(findingDetail) {
  const recommendations = findingDetail?.recommendations || [];
  return recommendations.length ? recommendations[0] : null;
}

function remediationSummary(recommendation) {
  return (
    recommendation?.summary
    || recommendation?.example_fix
    || recommendation?.description
    || 'Review the workflow change needed to address this finding.'
  );
}

function verificationSummary(recommendation) {
  return (
    recommendation?.expected_effect
    || 'Confirm the risky behavior is reduced in external validation and record the supporting result evidence.'
  );
}

export function TargetedValidationPreviewPanel({
  preview,
  loading,
  creating,
  error,
  onCreate,
  showCoverageNote = true,
}) {
  if (!loading && !preview && !error) {
    return null;
  }

  return (
    <div className="detail-card targeted-validation-preview">
      <strong>Targeted validation preview</strong>
      {loading ? <p>Preparing a deterministic preview from persisted evidence…</p> : null}
      {error ? <p className="overview-note">{error}</p> : null}
      {preview ? (
        <>
          {showCoverageNote && preview.coverage_note ? <p>{preview.coverage_note}</p> : null}
          <div className="detail-metrics-grid">
            <div className="detail-card detail-stat-card">
              <strong>{preview.role_counts.failure}</strong>
              <p>Failure items</p>
            </div>
            <div className="detail-card detail-stat-card">
              <strong>{preview.role_counts.near_miss}</strong>
              <p>Near-miss items</p>
            </div>
            <div className="detail-card detail-stat-card">
              <strong>{preview.role_counts.recovered}</strong>
              <p>Recovered items</p>
            </div>
            <div className="detail-card detail-stat-card">
              <strong>{preview.role_counts.control}</strong>
              <p>Control items</p>
            </div>
          </div>
          <ModeChips counts={preview.validation_mode_counts} />
          <ReferenceValidationGuidance reference={preview.reference} compact />
          <div className="form-actions targeted-validation-actions">
            <button className="primary-button" type="button" onClick={onCreate} disabled={creating}>
              {creating ? 'Creating…' : 'Create validation pack'}
            </button>
          </div>
        </>
      ) : null}
    </div>
  );
}

export function FindingTargetedValidationCard({
  selectedSystem,
  findingDetail,
  preview,
  previewLoading,
  actionLoading,
  actionError,
  onPreview,
  onCreate,
}) {
  const targetedValidation = fallbackFindingTargetedValidation(findingDetail.targeted_validation);
  const recommendation = primaryRecommendation(findingDetail);
  const reference = findingDetail.finding?.reference;
  const evidenceGuidance = buildTargetedValidationEvidenceGuidance(targetedValidation, reference);
  const hasActiveValidationPack = Boolean(targetedValidation.active_pack_id);
  const hasValidationRunEvidence = Boolean(targetedValidation.latest_result_summary) || targetedValidation.result_run_count > 0;

  if (targetedValidation.follow_up_route === 'external_validation') {
    return (
      <section className="detail-section">
        <h3>Targeted validation</h3>
        <div className="detail-card targeted-validation-card">
          <div className="workflow-mode-header">
            <strong>External validation</strong>
            <span className="chip targeted-validation-meta-chip">{externalValidationStatusLabel(targetedValidation)}</span>
          </div>
          <p>This finding does not yet support in-app validation pack generation. Validate the change externally using the guidance below.</p>
          <EvidenceNeededGuidanceCard
            canSay={evidenceGuidance.canSay}
            cannotSay={evidenceGuidance.cannotSay}
            nextSteps={evidenceGuidance.nextSteps}
            framed={false}
          />
          <ReferenceValidationGuidance reference={reference} />
          <div className="stack compact-stack">
            <article className="detail-card">
              <strong>Recommended remediation</strong>
              <p>{remediationSummary(recommendation)}</p>
            </article>
            <article className="detail-card">
              <strong>How to verify</strong>
              <p>{verificationSummary(recommendation)}</p>
            </article>
          </div>
        </div>
      </section>
    );
  }

  if (targetedValidation.follow_up_route === 'unavailable') {
    return (
      <section className="detail-section">
        <h3>Targeted validation</h3>
        <div className="detail-card targeted-validation-card">
          <div className="workflow-mode-header">
            <strong>Validation unavailable</strong>
            <span className="chip targeted-validation-meta-chip">Unavailable</span>
          </div>
          <p>{targetedValidation.eligibility_reason}</p>
          <EvidenceNeededGuidanceCard
            canSay={evidenceGuidance.canSay}
            cannotSay={evidenceGuidance.cannotSay}
            nextSteps={evidenceGuidance.nextSteps}
            framed={false}
          />
          <ReferenceValidationGuidance reference={reference} />
        </div>
      </section>
    );
  }

  return (
    <section className="detail-section">
      <h3>Targeted validation</h3>
      <p className="panel-copy">Prepare validation cases and attach results when available.</p>
      <div className="detail-card targeted-validation-card">
        <div className="workflow-mode-header">
          <strong>{eligibilityLabel(targetedValidation.eligibility_status)}</strong>
          <span className="chip targeted-validation-meta-chip">{preparednessLabel(targetedValidation.preparedness_status)}</span>
        </div>
        {hasActiveValidationPack || hasValidationRunEvidence ? (
          <ValidationRunStatusCard targetedValidation={targetedValidation} />
        ) : (
          <>
            <p>{targetedValidation.eligibility_reason}</p>
            <EvidenceNeededGuidanceCard
              canSay={evidenceGuidance.canSay}
              cannotSay={evidenceGuidance.cannotSay}
              nextSteps={evidenceGuidance.nextSteps}
              framed={false}
            />
            <ReferenceValidationGuidance reference={reference} />
            <ModeChips counts={targetedValidation.validation_mode_counts} />
          </>
        )}
        <div className="form-actions targeted-validation-actions">
          {targetedValidation.active_pack_id ? (
            <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/eval-packs/${targetedValidation.active_pack_id}`}>
              Open validation pack
            </a>
          ) : null}
          {targetedValidation.eligibility_status === 'eligible' && !targetedValidation.active_pack_id ? (
            <button className="secondary-button" type="button" onClick={onPreview} disabled={previewLoading}>
              {previewLoading ? 'Preparing preview…' : 'Preview validation pack'}
            </button>
          ) : null}
        </div>
        <TargetedValidationPreviewPanel
          preview={preview}
          loading={previewLoading}
          creating={actionLoading}
          error={actionError}
          onCreate={onCreate}
          showCoverageNote={false}
        />
      </div>
    </section>
  );
}

export function WorkflowTargetedValidationSection({
  selectedSystem,
  workflowDetail,
  preview,
  previewFindingId,
  previewLoadingFindingId,
  actionLoadingFindingId,
  actionError,
  onPreview,
  onCreate,
}) {
  const targetedValidationSummary = fallbackWorkflowTargetedValidationSummary(workflowDetail.targeted_validation_summary);
  const targetedValidationCandidates = workflowDetail.targeted_validation_candidates || [];

  return (
    <section className="detail-section">
      <h3>Targeted validation</h3>
      <p className="panel-copy">Prepare validation packs for eligible findings.</p>
      <div className="detail-metrics-grid">
        <div className="detail-card detail-stat-card">
          <strong>{targetedValidationSummary.eligible_finding_count}</strong>
          <p>Eligible findings</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{targetedValidationSummary.active_pack_count}</strong>
          <p>Active packs</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{targetedValidationSummary.packs_needing_labels_count}</strong>
          <p>Packs needing labels</p>
        </div>
        <div className="detail-card detail-stat-card">
          <strong>{targetedValidationSummary.ready_pack_count + targetedValidationSummary.validated_pack_count}</strong>
          <p>Ready or validated</p>
        </div>
      </div>
      {targetedValidationCandidates.length ? (
        <div className="stack compact-stack">
          {targetedValidationCandidates.map((candidate) => {
            const candidateGuidance = buildTargetedValidationEvidenceGuidance(
              {
                active_pack_id: candidate.active_pack_id,
                eligibility_reason: eligibilityLabel(candidate.eligibility_status),
                follow_up_route: 'in_app_pack',
                result_run_count: candidate.preparedness_status === 'validated' ? 1 : 0,
              },
              candidate.reference
            );
            return (
              <article className="detail-card targeted-validation-candidate" key={candidate.finding_id}>
                <div className="workflow-mode-header">
                  <strong>{candidate.title}</strong>
                  <span className="chip targeted-validation-meta-chip">{preparednessLabel(candidate.preparedness_status)}</span>
                </div>
                <p>{eligibilityLabel(candidate.eligibility_status)}</p>
                <EvidenceNeededGuidanceCard
                  title="Validation evidence needed"
                  canSay={candidateGuidance.canSay}
                  cannotSay={candidateGuidance.cannotSay}
                  nextSteps={candidateGuidance.nextSteps}
                  framed={false}
                />
                <ReferenceValidationGuidance reference={candidate.reference} compact />
                <ModeChips counts={candidate.validation_mode_counts} />
                <div className="form-actions targeted-validation-actions">
                  <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/findings/${candidate.finding_id}`}>
                    Open finding
                  </a>
                  {candidate.active_pack_id ? (
                    <a className="secondary-button action-link-button" href={`#/systems/${selectedSystem.id}/eval-packs/${candidate.active_pack_id}`}>
                      Open validation pack
                    </a>
                  ) : (
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => onPreview(candidate.finding_id)}
                      disabled={previewLoadingFindingId === candidate.finding_id}
                    >
                      {previewLoadingFindingId === candidate.finding_id ? 'Preparing preview…' : 'Preview pack'}
                    </button>
                  )}
                </div>
                {previewFindingId === candidate.finding_id || previewLoadingFindingId === candidate.finding_id ? (
                  <TargetedValidationPreviewPanel
                    preview={previewFindingId === candidate.finding_id ? preview : null}
                    loading={previewLoadingFindingId === candidate.finding_id}
                    creating={actionLoadingFindingId === candidate.finding_id}
                    error={actionError}
                    onCreate={() => onCreate(candidate.finding_id)}
                  />
                ) : null}
              </article>
            );
          })}
        </div>
      ) : (
        <div className="empty-state-card">
          <strong>No eligible targeted validation candidates yet</strong>
          <p>This workflow currently has no eligible findings with enough provenance to prepare a validation pack.</p>
        </div>
      )}
    </section>
  );
}
